import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Card, CardContent, Box, Divider, Stack, Typography } from '@mui/material';
import {
  CabDropdown, CabIcon, CabSwitch, CabDurationPicker, CabTextInput,
} from '@CabComponents';
import { User, ZoomSettings, PresetLocations, EventHoldTitle, EventHoldDescription } from '../../../store';
import { SLOT_INTERVAL_LABELS } from '../../../constants';
import { CabDurationUnitPicker } from '@CabComponents/CabDurationUnitPicker';
import CabMeetingLocationDropdown from '@CabComponents/CabMeetingLocationDropdown';
import { IoLockClosedOutline } from 'react-icons/io5';
import { useDebounce } from 'use-debounce';
import { SchedulingPreferences, SchedulingPreferencesList } from '../../../store/cabinetApi/generated/scheduling';

export interface MeetingDefaultsProps {
  user: User | null | undefined;
  schedulingPrefs: SchedulingPreferencesList | undefined;
  onSchedulingPrefChange: (pref: Partial<SchedulingPreferences>) => void;
  openUpgradeModal: () => void;
  presetLocations: PresetLocations;
  zoomSettings: {
    [settingsId: string]: ZoomSettings;
  };
  onAddZoom: () => void;
  hasMicrosoftGrant: boolean;
  hasGoogleGrant: boolean;
}

const SettingsSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <Stack sx={{
    gap: 0.25
  }}>
    <Typography
      sx={{
        fontSize: 16,
        fontWeight: 700
      }}>
      {title}
    </Typography>
    <Stack sx={{
      gap: 0
    }}>
      {children}
    </Stack>
  </Stack>
);

const SectionSpacer = () => <Box sx={{ pt: 1.75 }} />;

const SettingField = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box
    sx={{
      width: "100%",
      display: "flex",
      flexDirection: { xs: 'column', sm: 'row' },
      justifyContent: "space-between",
      alignItems: { xs: 'flex-start', sm: 'center' },
      gap: 1,
      paddingY: 1
    }}>
    <Typography>
      {label}
    </Typography>
    <Box
      sx={{
        display: "flex",
        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
        maxWidth: "100%",

        '& > *': {
          maxWidth: '100%',
        }
      }}>
      {children}
    </Box>
  </Box>
);

export const MeetingDefaults = ({
  user, schedulingPrefs, onSchedulingPrefChange, openUpgradeModal, presetLocations, zoomSettings, onAddZoom,
  hasMicrosoftGrant, hasGoogleGrant,
}: MeetingDefaultsProps): ReactElement | null => {
  const [override, setOverride] = useState(schedulingPrefs?.user_prefs?.meeting_organizer_override);
  const [debouncedOverride] = useDebounce(override, 800);

  const handleOverrideInputChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const value = e.target.value;
    setOverride(value);
  };

  useEffect(() => {
    if (schedulingPrefs?.user_prefs?.id) {
      onSchedulingPrefChange({
        id: schedulingPrefs.user_prefs.id,
        meeting_organizer_override: debouncedOverride
      });
    }
  }, [debouncedOverride, onSchedulingPrefChange, schedulingPrefs?.user_prefs?.id]);

  if (!user || !schedulingPrefs?.user_prefs) return null;

  return (
    <Card variant="outlined" sx={{ width: '100%', padding: 1 }}>
      <CardContent>
        <Stack sx={{
          gap: 2
        }}>
          <SettingsSection title="Availability Sharing">
            <SettingField label="Meeting start times will be offered">
              <CabDropdown<number>
                value={schedulingPrefs.user_prefs.default_slot_time_interval || 0}
                options={Object.keys(SLOT_INTERVAL_LABELS).map((interval) => ({
                  value: Number(interval),
                  label: SLOT_INTERVAL_LABELS[Number(interval)],
                }))}
                onChange={(e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                  default_slot_time_interval: e.target.value ? Number(e.target.value) : null
                })}
              />
            </SettingField>

            <Divider />
            <SettingField label="Meeting duration">
              <CabDurationPicker
                durationMinutes={schedulingPrefs.user_prefs.default_duration_minutes}
                onChange={(minutes) => schedulingPrefs.user_prefs && minutes && onSchedulingPrefChange({
                  id: schedulingPrefs.user_prefs.id,
                  default_duration_minutes: minutes,
                })}
                sx={{ marginTop: 1, marginBottom: 1 }}
              />
            </SettingField>

            <Divider />
            <SettingField label="Prevent conflicts for new meetings">
              <CabSwitch
                checked={schedulingPrefs.user_prefs.booking_check_conflicts}
                onChange={(e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                  id: schedulingPrefs.user_prefs.id,
                  booking_check_conflicts: e.target.checked,
                })}
              />
            </SettingField>

            <Divider />
            <SettingField label="Turn on Condense Times by default when sharing availability">
              <CabSwitch
                checked={schedulingPrefs.user_prefs.default_condense_slots}
                onChange={(e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                  id: schedulingPrefs.user_prefs.id,
                  default_condense_slots: e.target.checked,
                })}
              />
            </SettingField>

            <Divider />
            <SettingField label="Include links in times by default when sharing availability">
              <CabSwitch
                checked={schedulingPrefs.user_prefs.include_links_in_times ?? false}
                onChange={(e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                  id: schedulingPrefs.user_prefs.id,
                  include_links_in_times: e.target.checked,
                })}
              />
            </SettingField>
          </SettingsSection>

          <SectionSpacer />
          <SettingsSection title="Calendar Holds">
            <SettingField label="Title of calendar holds for reusable meetings">
              <CabDropdown<EventHoldTitle>
                value={EventHoldTitle[schedulingPrefs.user_prefs.event_title_for_holds]
                || EventHoldTitle.EVENT_TITLE}
                options={[
                  { value: EventHoldTitle.INTERNAL_LABEL, label: "Internal Label" },
                  { value: EventHoldTitle.EVENT_TITLE, label: "Event Title" },
                ]}
                onChange={(e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                  event_title_for_holds: e.target.value as EventHoldTitle
                })}
              />
            </SettingField>

            <Divider />
            <SettingField label="Description of calendar holds">
              <Box
                onClick={!user?.features.HOLD_DESCRIPTION_PREFERENCE ? openUpgradeModal : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                {!user?.features.HOLD_DESCRIPTION_PREFERENCE  && (
                  <CabIcon
                    Icon={IoLockClosedOutline}
                    alt='Locked'
                    slot='end'
                    sx={{ fontSize: 25, marginRight: 1 }}
                  />
                )}
                <CabDropdown<EventHoldDescription>
                  value={EventHoldDescription[schedulingPrefs.user_prefs.event_description_for_holds]
                  || EventHoldDescription.EVENT_DESCRIPTION}
                  options={[
                    { value: EventHoldDescription.INTERNAL_NOTES, label: "Internal Notes" },
                    { value: EventHoldDescription.EVENT_DESCRIPTION, label: "Event Description" },
                  ]}
                  disabled={!user?.features.HOLD_DESCRIPTION_PREFERENCE}
                  onChange={(e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                    event_description_for_holds: e.target.value as EventHoldDescription
                  })}
                />
              </Box>
            </SettingField>

            <Divider />
            <SettingField label="Include meeting link in calendar hold descriptions">
              <CabSwitch
                checked={schedulingPrefs.user_prefs.include_meeting_link_in_hold}
                onChange={(e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                  id: schedulingPrefs.user_prefs.id,
                  include_meeting_link_in_hold: e.target.checked,
                })}
              />
            </SettingField>

            <Divider />
            <SettingField label="Set automatic holds as busy on calendar">
              <CabSwitch
                checked={schedulingPrefs.user_prefs.meeting_holds_busy}
                onChange={(e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                  id: schedulingPrefs.user_prefs.id,
                  meeting_holds_busy: e.target.checked,
                })}
              />
            </SettingField>
          </SettingsSection>

          <SectionSpacer />
          <SettingsSection title="Calendar Invite">
            <SettingField label="Meeting location">
              <Box
                sx={{
                  maxWidth: 400,
                  minWidth: { xs: 0, sm: 300 },
                  width: "100%"
                }}>
                <CabMeetingLocationDropdown
                  value={schedulingPrefs.user_prefs.default_conference_provider !== 999
                    ? { conference: {
                      provider: schedulingPrefs.user_prefs.default_conference_provider || null, leader: null
                    } }
                    : schedulingPrefs.user_prefs.default_location_presets?.length ? {
                      presetLocation: schedulingPrefs.user_prefs.default_location_presets[0]
                    }
                      : { none: true }
                  }
                  onChange={loc => {
                    if (loc.conference || loc.conference === null) {
                      onSchedulingPrefChange({
                        default_conference_provider: loc.conference?.provider || null,
                        default_location_presets: [],
                      });
                    } else if (loc.presetLocation) {
                      onSchedulingPrefChange({
                        default_conference_provider: 999,
                        default_location_presets: [loc.presetLocation]
                      });
                    } else {
                      onSchedulingPrefChange({
                        default_conference_provider: 999,
                        default_location_presets: [],
                      });
                    }
                  }}
                  onBlurOtherField={() => console.log('N/A')}
                  showTokenInputs={false}
                  templateVars={{}}
                  user={user}
                  leaders={{
                    leaders: [],
                    orgLeaders: [],
                    orgSharedLeaderGrants: {},
                    orgLeaderCategories: {},
                    loaded: true,
                    orgLeadersLoaded: true
                  }}
                  zoomSettings={zoomSettings}
                  defaultZoomAccountLabel="Auto Select"
                  addUsernameToZoomLabel={false}
                  isOwner={true}
                  presetLocations={presetLocations}
                  meetingRooms={{}}
                  onAddZoom={onAddZoom}
                  disabledLocationPresetIds={[]}
                  disabledMeetingRoomIds={[]}
                  hideOtherOption
                  showCalendarDefaultOption
                  hasMicrosoftGrant={hasMicrosoftGrant}
                  hasGoogleGrant={hasGoogleGrant}
                />
              </Box>
            </SettingField>

            <Divider />
            <SettingField label="Include reschedule and cancel links in event descriptions">
              <CabSwitch
                checked={schedulingPrefs.user_prefs.allow_reschedule_cancel}
                onChange={(e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                  id: schedulingPrefs.user_prefs.id,
                  allow_reschedule_cancel: e.target.checked,
                })}
              />
            </SettingField>

            <Divider />
            <SettingField label="Include questions and answers in event descriptions">
              <CabSwitch
                checked={schedulingPrefs.user_prefs.show_questions_in_body}
                onChange={(e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                  id: schedulingPrefs.user_prefs.id,
                  show_questions_in_body: e.target.checked,
                })}
              />
            </SettingField>

            <Divider />
            <SettingField label="Hide Cabinet marketing message in meeting invite body">
              <Box sx={{
                display: 'flex'
              }}>
                {!user?.features.HIDE_PROMO && (
                  <CabIcon
                    Icon={IoLockClosedOutline}
                    alt='Locked'
                    slot='end'
                    sx={{ fontSize: 25, paddingTop: '4px' }}
                  />
                )}
                <CabSwitch
                  checked={schedulingPrefs.user_prefs.hide_promo_text}
                  onChange={ user?.features.HIDE_PROMO ?
                    (e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                      id: schedulingPrefs.user_prefs.id,
                      hide_promo_text: e.target.checked,
                    })
                    : openUpgradeModal
                  }
                />
              </Box>
            </SettingField>
          </SettingsSection>

          <SectionSpacer />
          <SettingsSection title="Booking Page">
            <SettingField label="Minimum lead time before meetings">
              <Box
                onClick={!user?.features.ADVANCE_NOTICE ? openUpgradeModal : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                {!user?.features.ADVANCE_NOTICE  && (
                  <CabIcon
                    Icon={IoLockClosedOutline}
                    alt='Locked'
                    slot='end'
                    sx={{ fontSize: 25, marginRight: 1 }}
                  />
                )}
                <CabDurationUnitPicker
                  durationMinutes={schedulingPrefs.user_prefs.default_start_delay_minutes}
                  onChange={(minutes) => schedulingPrefs.user_prefs && minutes >= 0 && onSchedulingPrefChange({
                    id: schedulingPrefs.user_prefs.id,
                    default_start_delay_minutes: minutes,
                  })}
                  disabled={!user?.features.ADVANCE_NOTICE}
                  sx={{ marginTop: 1, marginBottom: 1 }}
                />
              </Box>
            </SettingField>

            {user?.features.PARTICIPANT_REMINDERS && (
              <>
                <Divider />
                <SettingField label="Send reminder emails to all attendees before meeting">
                  <CabSwitch
                    checked={schedulingPrefs.user_prefs.send_participant_reminders}
                    onChange={(e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                      id: schedulingPrefs.user_prefs.id,
                      send_participant_reminders: e.target.checked,
                    })}
                  />
                </SettingField>

                {schedulingPrefs.user_prefs.send_participant_reminders && (
                  <>
                    <Divider />
                    <SettingField label="Time before meeting starts to send reminder emails">
                      <CabDurationUnitPicker
                        durationMinutes={schedulingPrefs.user_prefs.default_participant_reminder_minutes}
                        onChange={(mins) => schedulingPrefs.user_prefs && mins >= 0 && onSchedulingPrefChange({
                          id: schedulingPrefs.user_prefs.id,
                          default_participant_reminder_minutes: mins,
                        })}
                        sx={{ marginTop: 1, marginBottom: 1 }}
                      />
                    </SettingField>
                  </>
                )}
              </>
            )}

            <Divider />
            <SettingField label="Allow respondents to add participants to new meetings">
              <CabSwitch
                checked={schedulingPrefs.user_prefs.default_allow_add_participants}
                onChange={(e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                  id: schedulingPrefs.user_prefs.id,
                  default_allow_add_participants: e.target.checked,
                })}
              />
            </SettingField>

            {user.features.MEETING_ORGANIZER_OVERRIDE && (
              <>
                <Divider />
                <SettingField label="Meeting organizer shown on booking pages">
                  <CabTextInput
                    placeholder={`${user.first_name} ${user.last_name}`}
                    value={override}
                    onChange={handleOverrideInputChange}
                    sx={{ width: 300 }}
                  />
                </SettingField>
              </>
            )}
          </SettingsSection>
        </Stack>
      </CardContent>
    </Card>
  );
};
